/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  DevToolsEnhancerOptions,
  ListenerMiddleware,
  Store,
  StoreEnhancer,
  ThunkMiddleware,
  Tuple,
} from '@reduxjs/toolkit';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import type { CommonModuleState } from './common';
import { commonReducers } from './common';
import { commonListener } from './common/listener';
import { drawingToolReducer } from './drawingTool';
import { drawingToolListener } from './drawingTool/listener';
import type { DrawtoolModuleStore } from './drawingTool/reducer';
import {
  loadingIndicatorReducer,
  syncGroupsListener,
  syncGroupsReducer,
} from './generic';
import { genericListener } from './generic/listener';
import type { LoadingIndicatorModuleState } from './generic/loadingIndicator/types';
import type { SynchronizationGroupModuleState } from './generic/syncGroups/types';
import type { WebMapStateModuleState } from './map';
import { mapReducer, serviceReducer, layerReducer } from './map';
import { layersListener } from './map/layer/listeners';
import { mapListener } from './map/map/listener';
import { metronomeListener } from './map/map/metronomeListener';
import { serviceListener } from './map/service/listener';
import { openlayersListener } from './mapStore/openlayers/listener';
import { routerReducer } from './router';
import { routerListener } from './router/listener';
import { uiReducer } from './ui';
import type { UIModuleState } from './ui/types';
import type { ReducerMap } from './unifyReducerMaps';
import { unifyReducerMaps } from './unifyReducerMaps';

export const storeReducerMap = {
  webmap: mapReducer,
  services: serviceReducer,
  layers: layerReducer,
  syncGroups: syncGroupsReducer,
  loadingIndicator: loadingIndicatorReducer,
  ui: uiReducer,
  drawingtools: drawingToolReducer,
  router: routerReducer,
  common: commonReducers,
};

export const storeMiddlewares = [
  mapListener.middleware,
  serviceListener.middleware,
  layersListener.middleware,
  metronomeListener.middleware,
  syncGroupsListener.middleware,
  genericListener.middleware,
  drawingToolListener.middleware,
  routerListener.middleware,
  openlayersListener.middleware,
  commonListener.middleware,
];

const storeRootReducer = combineReducers(storeReducerMap);

export const createMockStore = (
  mockState?:
    | WebMapStateModuleState
    | SynchronizationGroupModuleState
    | LoadingIndicatorModuleState
    | UIModuleState
    | CommonModuleState
    | DrawtoolModuleStore,
): Store =>
  configureStore({
    reducer: storeRootReducer,
    preloadedState: mockState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(storeMiddlewares),
  });

export {
  getViewObjectForSyncGroup,
  getViewObjectForMapId,
  createViewObjectForNewMap,
} from './mapStore/openlayers/syncGroupViewStore';

/**
 * This creates an OpenGeoWeb redux store for redux connected components
 *
 * @param preloadedState Initial state for your application
 * @param extraReducers Additional reducers, you can pass your own reducers. See apps/geoweb/src/app/store.ts for how it is used in GeoWeb.
 * @param extraMiddleWares Additional middlewares
 * @param extraEnhancer Custom enhancers
 * @param enableDevTools Set to true to enable redux devtools.
 * @returns A redux store you can use with the core components
 */
export const configureOpenGeoWebStore = (
  preloadedState: object = {},
  extraReducers: ReducerMap = {},
  extraMiddleWares: ListenerMiddleware[] = [],
  extraEnhancers: StoreEnhancer[] = [],
  enableDevTools: boolean | DevToolsEnhancerOptions = false,
): Store => {
  const allReducers = unifyReducerMaps([storeReducerMap, extraReducers]);
  const openGeoWebReducers = combineReducers(allReducers);

  // Combine middlewares
  const middlewares: ListenerMiddleware[] = [
    mapListener.middleware,
    serviceListener.middleware,
    layersListener.middleware,
    genericListener.middleware,
    openlayersListener.middleware,
    ...extraMiddleWares,
  ] as unknown as ListenerMiddleware[];

  // Ensure we do not add middlewares twice
  const uniqueMiddlewares = Array.from(new Set(middlewares.map((obj) => obj)));

  return configureStore({
    reducer: openGeoWebReducers,
    preloadedState,
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers().concat(extraEnhancers),
    devTools: enableDevTools,
    middleware: (getDefaultMiddleware): Tuple<[ThunkMiddleware]> =>
      getDefaultMiddleware({
        serializableCheck: {
          warnAfter: 128,
        },
        immutableCheck: {
          warnAfter: 128,
        },
      }).concat(uniqueMiddlewares) as Tuple<[ThunkMiddleware]>,
  });
};
